import React, {useState} from 'react';
import {Link, Typography} from 'bonprix-design-system';
import Url from '@core/scripts/helper/url.js';
import WithdrawalElementContainer from '@account/pages/withdrawal/WithdrawalElementContainer';
import WithdrawalForm, {FormValues} from '@account/pages/withdrawal/WithdrawalForm';
import ConfirmationView from '@account/pages/withdrawal/ConfirmationView';
import {useTextsContext} from '@account/shared-components/providers/TextsProvider';
import WithdrawalTracking from '@account/pages/withdrawal/WithdrawalTracking';
import WithdrawalAlert, {
    FormState,
    WithdrawalMessageId,
    parseWithdrawalMessageId,
    isFullCancellation,
} from '@account/pages/withdrawal/WithdrawalAlert';

export interface WithdrawalPageContentProps {
    isCaptchaEnabled: boolean;
    isCaptchaV2Enabled?: boolean;
    friendlyCaptchaSiteKey: string;
    isApp: boolean;
    canNavigateBack: boolean;
    urls: {
        homeUrl: string;
        myAccountOverviewUrl: string;
        withdrawOrderUrl: string;
        serviceContactUrl: string;
    };
}

const WithdrawalPageContent = ({
    isCaptchaEnabled,
    isCaptchaV2Enabled,
    friendlyCaptchaSiteKey,
    isApp,
    canNavigateBack,
    urls,
}: WithdrawalPageContentProps) => {
    const {texts} = useTextsContext();

    const [formState, setFormState] = useState<FormState>(FormState.NOT_SUBMITTED);
    const [responseMessageId, setResponseMessageId] = useState<WithdrawalMessageId | undefined>(undefined);
    const [confirmationHeading, setConfirmationHeading] = useState<string>('');
    const [submittedData, setSubmittedData] = useState<Omit<FormValues, 'captchaToken'>>({
        firstName: '',
        lastName: '',
        email: '',
        orderNumber: '',
    });

    const handleErrorResponse = (orderNumber: string, messageId: string): void => {
        setFormState(FormState.ERROR);
        setSubmittedData((prev) => ({...prev, orderNumber}));
        setResponseMessageId(parseWithdrawalMessageId(messageId));
    };

    const handleSuccessResponse = (formData: FormValues, messageId: string): void => {
        setFormState(FormState.SUCCESS);
        setSubmittedData({...formData});
        const parsedMessageId = parseWithdrawalMessageId(messageId);
        setResponseMessageId(parsedMessageId);
        setConfirmationHeading(
            isFullCancellation(parsedMessageId)
                ? replaceParamWithOrderNumber(
                      texts['cus__withdrawal_confirmationHeading_text_cancelled'],
                      formData.orderNumber,
                  )
                : texts['cus__withdrawal_confirmationHeading_text_returnNeeded'],
        );
    };

    const replaceParamWithOrderNumber = (text: string, orderNumber?: string): string =>
        text.replace('{0}', orderNumber ?? submittedData.orderNumber);

    const replaceParamWithServiceContactLink = (text: string) => {
        const PARAM_PLACEHOLDER = '{0}';
        const textParts = text.split(PARAM_PLACEHOLDER);

        return (
            <>
                {textParts[0]}
                <Link
                    onClick={() => {
                        WithdrawalTracking.trackClickWithdrawCustomerSupport();
                    }}
                    href={Url.toAbsolute(urls.serviceContactUrl)}
                >
                    {texts.cus__serviceContact}
                </Link>
                {textParts[1]}
            </>
        );
    };

    return (
        <div className="bg-clr-bg-main-light pb-10">
            <WithdrawalAlert
                formState={formState}
                messageId={responseMessageId}
                orderNumber={submittedData.orderNumber}
                onClose={() => setFormState(FormState.NOT_SUBMITTED)}
            />
            <WithdrawalElementContainer className="bg-clr-bg-main-light">
                <Typography variant="bonprix-h4">{texts['cus__withdrawal_headline']}</Typography>
            </WithdrawalElementContainer>
            <WithdrawalElementContainer className="bg-clr-bg-main">
                {formState !== FormState.SUCCESS ? (
                    <React.Fragment key="withdrawal-form-view">
                        <Typography>
                            {replaceParamWithServiceContactLink(texts.cus__withdrawal_heading_text)}
                        </Typography>
                        <WithdrawalForm
                            isCaptchaEnabled={isCaptchaEnabled}
                            isCaptchaV2Enabled={isCaptchaV2Enabled}
                            friendlyCaptchaSiteKey={friendlyCaptchaSiteKey}
                            withdrawOrderUrl={urls.withdrawOrderUrl}
                            isApp={isApp}
                            canNavigateBack={canNavigateBack}
                            homeUrl={urls.homeUrl}
                            onSuccess={handleSuccessResponse}
                            onError={handleErrorResponse}
                        />
                    </React.Fragment>
                ) : (
                    <React.Fragment key="withdrawal-confirmation-view">
                        <Typography>{confirmationHeading}</Typography>
                        <ConfirmationView
                            withdrawalData={submittedData}
                            homeUrl={urls.homeUrl}
                            myAccountUrl={urls.myAccountOverviewUrl}
                        />
                    </React.Fragment>
                )}
            </WithdrawalElementContainer>
        </div>
    );
};

export default WithdrawalPageContent;
